import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { getPaymentStatus, type Payment } from '../api/payment';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const merchantOid = searchParams.get('merchant_oid');
  
  const [payment, setPayment] = useState<Payment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent double fetch in React.StrictMode (development)
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const fetchPaymentDetails = async () => {
      if (merchantOid) {
        try {
          const paymentData = await getPaymentStatus(merchantOid);
          setPayment(paymentData);
        } catch (error) {
          console.error('Failed to fetch payment details:', error);
        }
      }
      setIsLoading(false);
    };

    fetchPaymentDetails();
  }, [merchantOid]);

  // Otomatik yönlendirme
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Success Animation */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-green-500/20 rounded-full blur-3xl" />
          <div className="relative bg-green-500/10 p-8 rounded-full inline-block border border-green-500/30">
            <CheckCircle2 className="w-20 h-20 text-green-400" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-white mb-4">
          Ödeme Başarılı!
        </h1>

        {/* Description */}
        <p className="text-slate-400 mb-8">
          Aboneliğiniz başarıyla aktif edildi. Artık tüm özelliklere erişebilirsiniz.
        </p>

        {/* Payment Details */}
        {payment && (
          <div className="bg-slate-900 rounded-xl p-6 mb-8 border border-slate-800 text-left">
            <h3 className="text-sm font-medium text-slate-400 mb-4">
              Ödeme Detayları
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-500">Sipariş No</span>
                <span className="text-white font-mono text-sm">
                  {payment.merchant_oid}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tutar</span>
                <span className="text-white">
                  {payment.amount.toFixed(2)} {payment.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Plan</span>
                <span className="text-white capitalize">
                  {payment.module_type} - {payment.tier}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Periyot</span>
                <span className="text-white">
                  {payment.billing_cycle === 'yearly' ? 'Yıllık' : 'Aylık'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* CTA Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          Dashboard'a Git
          <ArrowRight className="w-5 h-5" />
        </button>

        {/* Auto redirect notice */}
        <p className="mt-4 text-sm text-slate-500">
          5 saniye içinde otomatik yönlendirileceksiniz...
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;

